import React, { useState, useEffect } from 'react';
import { Clock, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { services as fallbackServices, businessInfo } from '../data/mock';
import BookingCalendar from './BookingCalendar';
import axios from 'axios';
import { API } from '../config';

const ServicesSection = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [services, setServices] = useState(fallbackServices);

  useEffect(() => {
    axios.get(`${API}/services`)
      .then(res => { if (res.data?.length) setServices(res.data); })
      .catch(() => {});
  }, []);

  const categories = [
    { id: 'all', name: 'All' },
    { id: 'express', name: 'Express' },
    { id: 'relaxing', name: 'Relaxing' },
    { id: 'deep-tissue', name: 'Deep Tissue' },
    { id: 'premium', name: 'Premium' },
    { id: 'reflexology', name: 'Reflexology' },
    { id: 'couples', name: 'Couples' },
  ];

  const filteredServices = activeCategory === 'all' 
    ? services 
    : services.filter(s => s.category === activeCategory);

  return (
    <section id="services" className="py-28 bg-bone relative">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-luxury-gold text-xs uppercase tracking-[0.3em] font-medium">Our Treatments</span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-luxury-green mt-4 mb-6">
            Therapeutic Massage
            <span className="block italic text-luxury-green/70 mt-1">Tailored to You</span>
          </h2>
          <p className="text-stone-500 font-light leading-relaxed">
            From quick relief to extended therapeutic treatments, personalized care for your wellbeing.
            Every treatment is delivered by a Senior Therapist.
          </p>
          <div className="inline-flex items-center gap-2 bg-luxury-green/10 border border-luxury-green/20 rounded-full px-5 py-2 mt-4" data-testid="services-pay-full-note">
            <span className="text-sm text-luxury-green font-medium">Pay in full online &amp; save <strong>15%</strong> &middot; or secure with a &euro;20 deposit &amp; save 5%</span>
          </div>
        </div>

        {/* Filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-14">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-6 py-2.5 rounded-full text-xs uppercase tracking-[0.15em] font-medium transition-all duration-300 ${
                activeCategory === cat.id
                  ? 'bg-luxury-green text-white shadow-lg shadow-luxury-green/20'
                  : 'bg-white text-stone-500 hover:text-luxury-green border border-stone-200'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredServices.map((service, index) => (
            <div
              key={service.id}
              className="group bg-white rounded-2xl p-6 hover:shadow-xl transition-all duration-500 hover:-translate-y-1 border border-stone-100 hover:border-luxury-gold/20"
            >
              <div className="flex justify-between items-start mb-4">
                <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] font-medium text-luxury-green bg-sage-50 px-3 py-1.5 rounded-full">
                  {service.category === 'premium' && <Sparkles size={10} />}
                  {service.category?.replace('-', ' ')}
                </span>
                <div className="flex items-center gap-1.5 text-stone-400">
                  <Clock size={12} strokeWidth={1.5} />
                  <span className="text-xs">{service.duration}</span>
                </div>
              </div>

              <h3 className="font-display text-lg text-stone-800 mb-2 group-hover:text-luxury-green transition-colors">
                {service.name}
              </h3>

              {service.note && (
                <p className="text-[10px] text-luxury-gold bg-luxury-gold/5 px-3 py-1.5 rounded-lg mb-4 border border-luxury-gold/10">
                  {service.note}
                </p>
              )}

              <div className="flex items-center justify-between pt-4 border-t border-stone-100">
                <div>
                  <span className="font-display text-2xl text-luxury-green">
                    €{service.price}
                    {service.name.toLowerCase().includes('couple') && (
                      <span className="text-xs font-sans font-normal text-stone-400 ml-1">per person</span>
                    )}
                  </span>
                  <p className="text-[9px] text-luxury-gold mt-0.5">5% off when you pay online</p>
                </div>
                <Button
                  onClick={() => setIsBookingOpen(true)}
                  variant="ghost"
                  className="text-luxury-green hover:text-luxury-green/80 hover:bg-sage-50 text-xs uppercase tracking-[0.1em]"
                >
                  Book
                  <ArrowRight size={12} className="ml-1" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-20">
          <div className="inline-block border-t border-stone-200 pt-8">
            <p className="text-stone-400 text-sm mb-6 font-light">Ready for your experience?</p>
            <Button
              onClick={() => setIsBookingOpen(true)}
              className="bg-luxury-green hover:bg-luxury-green/90 text-white px-10 py-6 rounded-full text-xs uppercase tracking-[0.2em] font-medium shadow-lg shadow-luxury-green/15 hover:shadow-xl transition-all"
            >
              Book Online
            </Button>
          </div>
        </div>
      </div>

      <BookingCalendar isOpen={isBookingOpen} onClose={() => setIsBookingOpen(false)} />
    </section>
  );
};

export default ServicesSection;
